// src/pages/admin/subCategoryManagement.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { Delete, Edit, Add, Search, FilterList, ArrowBack, Close } from '@mui/icons-material';
 
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
 
// FIXED: Import from subCategoryApi.ts (with capital C)
import {
  useListSubCategoriesQuery,
  useDeleteSubCategoryMutation,
  useGetSubCategoryCountQuery,
  useAddSubCategoryMutation,
  useUpdateSubCategoryMutation,
} from '../../features/admin/subcategoryApi.ts';  // Changed from subcategoryApi.ts to subCategoryApi.ts
 
import { useListCategoriesQuery } from '../../features/admin/categoryApi.ts';
 
import {
  SubCategory,
  SubCategoryFormData,
  SubCategoryFormErrors,
  SubCategorySnackbarState,
  SUB_CATEGORY_API_CONFIG,
} from '../../features/admin/types.ts';

// FIXED: Add BASE_URL constant
const BASE_URL = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');

// FIXED: This function is not needed since we're using RTK Query mutations
// but keeping it for reference - can be removed

const SubCategoryManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
 
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<SubCategorySnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState<SubCategory | null>(null);
  const [formData, setFormData] = useState<SubCategoryFormData>({
    sub_cat_name: '',
    sub_cat_short_name: '',
    sub_cat_desc: '',
    cat_id: 0,
    status: 1,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [formErrors, setFormErrors] = useState<SubCategoryFormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
 
  const subCategoriesPerPage = 10;
 
  const {
    data: subCategoriesResponse,
    isLoading,
    error,
    isError,
    refetch
  } = useListSubCategoriesQuery({
    page,
    limit: subCategoriesPerPage,
    search_str: searchStr
  });
 
  const {
    data: countResponse,
    refetch: refetchCount
  } = useGetSubCategoryCountQuery({ search_str: searchStr });
 
  const {
    data: categoriesResponse,
    isLoading: isCategoriesLoading
  } = useListCategoriesQuery({
    page: 1,
    limit: 1000,
    search_str: ''
  });
 
  const [deleteSubCategory, { isLoading: isDeleting }] = useDeleteSubCategoryMutation();
  const [addSubCategory] = useAddSubCategoryMutation();
  const [updateSubCategory] = useUpdateSubCategoryMutation();
 
 
  const subCategories = subCategoriesResponse?.result || subCategoriesResponse?.data || [];
  const totalRecords = countResponse?.count || subCategoriesResponse?.count || 0;
  const totalPages = Math.ceil(totalRecords / subCategoriesPerPage);
  const categories = categoriesResponse?.result || categoriesResponse?.data || [];
 
  const isEditing = Boolean(editingSubCategory);
 
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      refetchCount();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchStr, refetchCount]);
 
  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };
 
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
 
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };
 
  const resetForm = () => {
    setFormData({
      sub_cat_name: '',
      sub_cat_short_name: '',
      sub_cat_desc: '',
      cat_id: 0,
      status: 1,
    });
    setSelectedImage(null);
    setImagePreview('');
    setFormErrors({});
    setEditingSubCategory(null);
  };
 
  const handleAddSubCategory = () => {
    resetForm();
    setShowForm(true);
  };
 
  const handleEditSubCategory = (subCategory: SubCategory) => {
    setFormData({
      sub_cat_name: subCategory.sub_cat_name || '',
      sub_cat_short_name: subCategory.sub_cat_short_name || '',
      sub_cat_desc: subCategory.sub_cat_desc || '',
      cat_id: subCategory.cat_id || 0,
      status: subCategory.status || 1,
    });
    // FIXED: Use subCategory.sub_cat_main_image (not sub_cat_main_image directly)
    setImagePreview(
      subCategory.sub_cat_main_image
        ? `${BASE_URL}/images/${subCategory.sub_cat_main_image}`
        : ''
    );
    setEditingSubCategory(subCategory);
    setShowForm(true);
  };
 
  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };
 
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
 
  const handleCategoryChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, cat_id: Number(e.target.value) }));
    if (formErrors.cat_id) {
      setFormErrors(prev => ({ ...prev, cat_id: '' }));
    }
  };
 
  const handleStatusChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, status: Number(e.target.value) }));
  };
 
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Please select a valid image file (JPEG, PNG, or GIF)',
          severity: 'error'
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'Image size should be less than 5MB',
          severity: 'error'
        });
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
 
  const validateForm = (): boolean => {
    const newErrors: SubCategoryFormErrors = {};
    if (!formData.sub_cat_name.trim()) {
      newErrors.sub_cat_name = 'Sub category name is required';
    }
    if (!formData.sub_cat_short_name.trim()) {
      newErrors.sub_cat_short_name = 'Sub category short name is required';
    }
    if (!formData.cat_id || formData.cat_id === 0) {
      newErrors.cat_id = 'Parent category is required';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
 
    setIsFormSubmitting(true);
 
    try {
      // FIXED: Build payload correctly
      const payload: any = {
        cat_id: formData.cat_id,
        sub_cat_name: formData.sub_cat_name,
        sub_cat_short_name: formData.sub_cat_short_name,
        status: formData.status,
      };
      
      // Add optional fields
      if (formData.sub_cat_desc?.trim()) {
        payload.sub_cat_desc = formData.sub_cat_desc;
      }
      
      // Add image if selected
      if (selectedImage) {
        payload.sub_cat_main_image = selectedImage;
      }
      
      // Add ID for update
      if (isEditing && editingSubCategory) {
        payload.sub_cat_id = editingSubCategory.sub_cat_id || editingSubCategory.id;
      }
 
      const result = isEditing
        ? await updateSubCategory(payload).unwrap()
        : await addSubCategory(payload).unwrap();
 
      setSnackbar({
        open: true,
        message: result.message || `Sub category ${isEditing ? 'updated' : 'added'} successfully`,
        severity: 'success',
      });
 
      handleBackToList();
      refetch();
      refetchCount();
    } catch (err: any) {
      console.error('Submit error:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Operation failed',
        severity: 'error',
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (subCategoryId: string | number) => {
    if (window.confirm('Are you sure you want to delete this sub category?')) {
      try {
        const result = await deleteSubCategory({ sub_cat_id: Number(subCategoryId) }).unwrap();
        setSnackbar({
          open: true,
          message: result.message || 'Sub category deleted successfully!',
          severity: 'success'
        });
        refetch();
        refetchCount();
      } catch (err: any) {
        console.error('Delete error:', err);
        setSnackbar({
          open: true,
          message: err?.data?.message || err?.message || 'Error deleting sub category.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderStatus = (status: number) => {
    switch (status) {
      case 1:
        return <Chip label="Active" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />;
      case 0:
        return <Chip label="Inactive" size="small" sx={{ bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600, fontSize: '12px' }} />;
      default:
        return <Chip label="Unknown" size="small" />;
    }
  };

  const getCategoryName = (catId: number): string => {
    const category = categories.find(cat => (cat.cat_id || cat.id) === catId);
    return category ? category.cat_name : `Category ${catId}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Sub Categories
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleAddSubCategory}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add sub category
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={(e, v) => setTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${totalRecords})`} />
                      {/* <Tab label="Active" />
                      <Tab label="Inactive" /> */}
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search sub categories"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{ 
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#c9cccf',
                        color: '#202223',
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent',
                        }
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load sub categories. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && subCategories.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No sub categories found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first sub category'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && subCategories.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 80px 1fr 200px 200px 150px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}></Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Image</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Sub Category</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Short Name</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Parent Category</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>Status</Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Actions</Typography>
                      </Box>

                      {subCategories.map((subCategory: SubCategory, index: number) => (
                        <Box
                          key={subCategory.sub_cat_id ?? subCategory.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 80px 1fr 200px 200px 150px 100px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < subCategories.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>
                            {(page - 1) * subCategoriesPerPage + index + 1}
                          </Typography>

                          <Avatar
                            src={subCategory.sub_cat_main_image ? 
                            `${BASE_URL}/images/${subCategory.sub_cat_main_image}` : undefined}
                            variant="rounded"
                            sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}
                          >
                            {subCategory.sub_cat_short_name?.[0] || subCategory.sub_cat_name?.[0] || '?'}
                          </Avatar>

                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                            {subCategory.sub_cat_name || 'N/A'}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {subCategory.sub_cat_short_name || 'N/A'}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {getCategoryName(subCategory.cat_id)}
                          </Typography>

                          <Box>
                            {renderStatus(subCategory.status)}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSubCategory(subCategory)}
                              sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(subCategory.sub_cat_id ?? subCategory.id)}
                              disabled={isDeleting}
                              sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Sub Categories
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit sub category' : 'Add sub category'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Sub category details
                            </Typography>

                            <FormControl 
                              fullWidth 
                              disabled={isFormSubmitting || isCategoriesLoading} 
                              error={!!formErrors.cat_id}
                              sx={{ mb: 2 }}
                            >
                              <InputLabel>Parent Category *</InputLabel>
                              <Select
                                value={formData.cat_id.toString()}
                                onChange={handleCategoryChange}
                                label="Parent Category *"
                              >
                                <MenuItem value="0">
                                  <em>Select a category</em>
                                </MenuItem>
                                {categories.map((category) => (
                                  <MenuItem key={category.cat_id || category.id} value={(category.cat_id || category.id).toString()}>
                                    {category.cat_name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {formErrors.cat_id && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                  {formErrors.cat_id}
                                </Typography>
                              )}
                            </FormControl>

                            <TextField
                              name="sub_cat_name"
                              label="Sub category name"
                              value={formData.sub_cat_name}
                              onChange={handleInputChange}
                              error={!!formErrors.sub_cat_name}
                              helperText={formErrors.sub_cat_name}
                              fullWidth
                              required
                              placeholder="e.g., LED TV"
                              disabled={isFormSubmitting}
                              sx={{ mb: 2 }}
                            />

                            <TextField
                              name="sub_cat_short_name"
                              label="Short name"
                              value={formData.sub_cat_short_name}
                              onChange={handleInputChange}
                              error={!!formErrors.sub_cat_short_name}
                              helperText={formErrors.sub_cat_short_name || 'Maximum 10 characters'}
                              fullWidth
                              required
                              placeholder="e.g., LED"
                              inputProps={{ maxLength: 10 }}
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Media
                            </Typography>
                            
                            <Box sx={{ 
                              border: '2px dashed #c9cccf', 
                              borderRadius: '12px', 
                              p: 4,
                              textAlign: 'center',
                              cursor: 'pointer',
                              bgcolor: '#fafbfb',
                              transition: 'all 0.2s',
                              '&:hover': { 
                                borderColor: '#202223',
                                bgcolor: '#f6f6f7'
                              }
                            }}
                            onClick={() => document.getElementById('image-upload')?.click()}>
                              {imagePreview ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <Avatar
                                    src={imagePreview}
                                    variant="rounded"
                                    sx={{ width: 120, height: 120, mx: 'auto' }}
                                  />
                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      bgcolor: 'white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImage(null);
                                      setImagePreview('');
                                    }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box>
                                  <Box sx={{ 
                                    width: 56, 
                                    height: 56, 
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    border: '1px solid #e1e3e5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                  }}>
                                    <Add sx={{ fontSize: 24, color: '#6d7175' }} />
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      textTransform: 'none',
                                      borderColor: '#c9cccf',
                                      color: '#202223',
                                      fontWeight: 500,
                                      borderRadius: '8px',
                                      '&:hover': {
                                        borderColor: '#202223',
                                        bgcolor: 'transparent',
                                      }
                                    }}
                                  >
                                    Add image
                                  </Button>
                                  <Typography variant="caption" display="block" sx={{ color: '#6d7175', mt: 1 }}>
                                    Accepts images, videos, or 3D models
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            <input
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              style={{ display: 'none' }}
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Description
                            </Typography>
                            <TextField
                              name="sub_cat_desc"
                              value={formData.sub_cat_desc}
                              onChange={handleInputChange}
                              fullWidth
                              multiline
                              rows={5}
                              placeholder="Enter sub category description"
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControl fullWidth disabled={isFormSubmitting}>
                            <Select
                              value={formData.status.toString()}
                              onChange={handleStatusChange}
                              sx={{ borderRadius: '8px' }}
                            >
                              <MenuItem value={1}>Active</MenuItem>
                              <MenuItem value={0}>Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#eeeeee',
                        bgcolor: '#f44336',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#eeeeee',
                        bgcolor: '#F78E12',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting || !formData.sub_cat_name.trim() || !formData.sub_cat_short_name.trim() || !formData.cat_id}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubCategoryManagement;